import { useCallback, useState } from 'react';

import { DEFAULT_MAP_FILTER_STATE } from '@/components/map/utils/mapFilters';
import type { MapFilterState } from '@/components/map/utils/mapFilters';
import {
    clearPersistedMapFilters,
    loadPersistedMapFilters,
    savePersistedMapFilters,
} from '@/components/map/utils/mapFilterPersistence';

interface UseMapFiltersOptions {
    /**
     * URL-derived filter state from `buildInitialMapStateFromSearch`.
     * Non-null takes priority over localStorage.
     * Null means no URL filter state — localStorage restore applies.
     */
    initialFromUrl?: { isActive: boolean; committed: MapFilterState } | null;
}

export function useMapFilters({ initialFromUrl }: UseMapFiltersOptions = {}) {
    // Read initial values exactly once.
    // Priority: URL state (non-null) > localStorage > defaults.
    const [{ initialIsActive, initialCommitted }] = useState<{
        initialIsActive: boolean;
        initialCommitted: MapFilterState;
    }>(() => {
        if (initialFromUrl != null) {
            return { initialIsActive: initialFromUrl.isActive, initialCommitted: initialFromUrl.committed };
        }
        const persisted = loadPersistedMapFilters();
        if (persisted?.isActive) {
            return { initialIsActive: true, initialCommitted: persisted.committed };
        }
        return { initialIsActive: false, initialCommitted: DEFAULT_MAP_FILTER_STATE };
    });

    const [committed, setCommitted] = useState<MapFilterState>(initialCommitted);
    const [draft, setDraft] = useState<MapFilterState>(initialCommitted);
    // Tracks whether the user has explicitly applied filters. When false, the map
    // shows all POIs regardless of the committed filter values.
    const [isActive, setIsActive] = useState<boolean>(initialIsActive);

    // Commits the current draft, marks filters as active, and persists the state.
    const applyFilters = useCallback(() => {
        setCommitted(draft);
        setIsActive(true);
        savePersistedMapFilters({ isActive: true, committed: draft });
    }, [draft]);

    // Resets draft and committed to defaults, deactivates filtering, and clears storage.
    const resetFilters = useCallback(() => {
        setDraft(DEFAULT_MAP_FILTER_STATE);
        setCommitted(DEFAULT_MAP_FILTER_STATE);
        setIsActive(false);
        clearPersistedMapFilters();
    }, []);

    // Called when the panel opens or closes without Apply.
    // When no active filters: resets draft to defaults so the panel shows a clean slate.
    // When filters are active: syncs draft from committed so the panel shows the applied values.
    // Draft-only changes and panel open/close do not write to storage.
    const discardDraft = useCallback(() => {
        setDraft(isActive ? committed : DEFAULT_MAP_FILTER_STATE);
    }, [isActive, committed]);

    // Applied by useMapUrlSync on back/forward navigation to restore URL-derived filter state.
    // Updates committed + draft together and syncs localStorage.
    const setFromUrl = useCallback((urlState: { isActive: boolean; committed: MapFilterState } | null) => {
        const target = urlState ?? { isActive: false, committed: DEFAULT_MAP_FILTER_STATE };
        setDraft(target.committed);
        setCommitted(target.committed);
        setIsActive(target.isActive);
        if (target.isActive) {
            savePersistedMapFilters({ isActive: true, committed: target.committed });
        } else {
            clearPersistedMapFilters();
        }
    }, []);

    return { committed, draft, setDraft, applyFilters, resetFilters, discardDraft, isActive, setFromUrl };
}
